import {Group, Modal, Paper, Space, Switch, Tooltip} from "@mantine/core";
import { Eye as IconEye } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Link} from "react-router";
import withRouteErrorBoundary from "../../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Col, Row} from "../../../components/Flex/Flex";
import Icon from "../../../components/Icon/Icon.tsx";
import IconMap from "../../../components/Icon/IconMap.ts";
import Page from "../../../components/Page/Page.tsx";
import style from '../../../style/Map.module.css';
import api, {useBHSystems, useSWCLocation, useUser} from "../../../utils/api.ts";
import {modalNoBlockZoom} from "../../../utils/modals";
import {generateSystemPlan} from "../../../utils/TravelPlanner.ts";
import {bodyExpiration} from "../ContractList/ContractList.jsx";
import {UserLocation, getPos, scanDirs, getBHTranslate} from "../../../utils/MapUtils.tsx";
import {bodyReward, bodyType} from "../ContractList/Formatters.jsx";
import BestScansDialog from "./BestScansDialog";

const sortAvg = (a, b) => b.avg - a.avg;

const getEmptyStats = () => ({sure: 0, avg: 0, sight: 0, norm: 0, list: []});

function HeatMap() {
    const {systems} = useBHSystems();
    const [contracts, setContracts] = useState(null);
    const [systemDetails, setSystemDetails] = useState(null);
    const [showScanLines, setShowScanLines] = useState(true);
    const {location: userLocation} = useSWCLocation();
    const {user} = useUser();
    const [hoverSystemId, setHoverSystemId] = useState(null);

    useEffect(() => {
        api.get('/bh/contracts').then(res => {
            setContracts(res.data);
        })
    }, []);

    const locations = useMemo(() => {
        if (contracts === null) {
            return [];
        }
        let found = {};
        contracts.forEach(contract => {
            for (let i = 0; i < contract.locations.length; i++) {
                let l = contract.locations[i];
                let c = {...contract};
                if (!found[l.id]) {
                    found[l.id] = getEmptyStats();
                }
                let chance = 1 / contract.locations.length;
                c.chance = chance;
                if (contract.sighting?.location.system.id === l.id) {
                    // increase chance by 50%, without going over 100%
                    chance = Math.min(1, chance + 0.5);
                    found[l.id].sight += 1;
                    c.sighted = true;
                } else {
                    c.sighted = false;
                }

                found[l.id].avg += chance;
                if (contract.locations.length === 1) {
                    c.sure = true;
                    found[l.id].sure += 1;
                } else {
                    c.sure = false;
                }

                found[l.id].list.push(c);
            }
            if(contract.locations.length === 0 && contract.sighting) {
                let {id} = contract.sighting.location.system;
                let c = {...contract};
                c.chance = 0.5;
                c.sighted = true;
                c.sure = false;

                if (!found[id]) {
                    found[id] = getEmptyStats();
                }

                found[id].list.push(c);
                found[id].sight += 1;
                found[id].avg += c.chance;
            }
        });

        let max = Object.values(found).reduce((m, f) => Math.max(m, f.avg), 2);

        for (let key in found) {
            found[key].norm = found[key].avg / max;
        }
        return systems.map(system => ({...system, ...(found[system.id] ?? getEmptyStats())})).sort(sortAvg);
    }, [systems, contracts]);

    return (
        <Page>
            <title>Contract Heatmap</title>
            <Row align="center" wrap={false}>
                <Col grow={1} shrink={0}>
                    <h2>Target Heatmap</h2>
                </Col>
                {userLocation ? <Col><Switch id="toggle-lines" checked={showScanLines} onChange={e => setShowScanLines(e.currentTarget.checked)} label="Show current scan lines" /></Col> : null}
            </Row>
            <Paper className="mt-4">
                <Row className="flex-column md:flex-row" align="stretch">
                    <Col grow={100} shrink={1} className={style.wrapper}>
                        {showScanLines ? <HeatMapCanvas current={userLocation} showCurrentLines={true}/> : null}
                        <div className={style.dotWrapper}>
                            {locations.map(system => <HeatDot key={system.id} system={system} openModal={setSystemDetails} highlighted={hoverSystemId === system.id}/>)}
                            {userLocation ? <UserLocation location={userLocation} /> : null}
                        </div>
                    </Col>
                    <Col grow={1} shrink={0} basis="200px">
                        <h3 className="mt-1">Best Locations</h3>
                        {locations.slice(0, 12).map(l => <Location key={l.id} system={l} openModal={setSystemDetails} setHover={setHoverSystemId}/>) }
                    </Col>
                </Row>
            </Paper>
            {systemDetails !== null ? <SystemDetails system={systemDetails} onHide={() => setSystemDetails(null)}/> : null}
            <Space h="md" />
            {user.subLevel > 0 && (userLocation !== null ? <BestScansDialog  contracts={contracts} /> : <Paper className="mt-4"><i>Best scans not available without a location. If you are not in hyper check <Link to="/user/api-permissions">your permissions</Link></i></Paper>)}
        </Page>
    )
}


const HeatDot = ({system, openModal, highlighted}) => {
    let alpha = system.avg > 0 ? '' : style.dotInactive;
    let colors = {
        background: "hsl(" + ((1 - system.norm) * 130) + ", 100%, 40%)",
        borderColor: "hsl(" + ((1 - system.norm) * 130) + ", 100%, 20%)",
        boxShadow: "0 0 var(--blur) 2px hsl(" + ((1 - system.norm) * 130) + ", 100%, 30%)"
    }
    let {mx, my} = getBHTranslate(system);
    return <div className={style.system} style={{bottom: getPos(system.galY+my), left: getPos(system.galX+mx)}} title={`${system.name} (${system.galX}, ${system.galY})`} onClick={() => openModal(system)}>
        <div className={`${style.dotHeat} ${alpha} ${highlighted ? style.dotHighlight : ''}`} style={colors}/>
    </div>
}

const Location = ({system, openModal, setHover}) => {
    let nid = system.id.substring(2);
    const {location} = useSWCLocation();
    return (
        <>
            <Row onMouseEnter={() => setHover(system.id)} onMouseLeave={() => setHover(null)}>
                <Col grow={1}>
                    <b className="clickable" onClick={() => openModal(system)}>{system.name}</b><br/>
                </Col>
                <Col>
                    <Group>
                    <a href={`https://www.swcombine.com/rules/?Galaxy_Map&systemID=${nid}`}
                       target="_blank" className="travel-planner ml-3" title="System details" rel="noreferrer"><Icon icon={IconMap.SwcRules} /></a>
                    <a href={generateSystemPlan(system.galX, system.galY, location)}
                       target="_blank" className="travel-planner ml-3" title="Travel Planner" rel="noreferrer"><Icon icon={IconMap.TravelPlanner} /></a>
                    </Group>
                </Col>
            </Row>
            <Row mb={3}>
                <Col grow={1}>
                    <small>({system.galX}, {system.galY})</small>
                </Col>
                <Col>
                    <small title="Targets found">{system.avg.toFixed(2)}</small>
                </Col>
            </Row>
        </>
    )
}


const SystemDetails = ({system, onHide}) => {
    const bodyName = useCallback(row => {
        return <Link className="contract-name" to={`/bh/contract/${row.id}`} state={{prev: 'heatmap'}}>{row.name}</Link>;
    }, []);

    const bodyChance = useCallback(row => {
        if (row.sure) {
            return <span className="text-green">Found</span>
        }
        let foundIcon = null;
        if (row.sighting) {
            if(row.sighting.location.system.id === system.id) {
                return <span className="text-yellow">Sighted</span>
            } else {
                foundIcon = <Tooltip label="Target sighted elsewhere"><IconEye size={16} className="text-yellow" /></Tooltip>
            }
        }
        return <div>{(row.chance * 100).toFixed(0)}% {foundIcon}</div>;
    }, [system.id]);

    return (
        <Modal
            opened={true}
            onClose={onHide}
            title={`${system.name} (${system.galX}, ${system.galY})`}
            size="xl"
            removeScrollProps={modalNoBlockZoom}
        >
            <DataTable
                records={system.list}
                idAccessor="id"
                columns={[
                    {accessor: 'name', title: 'Target Name', render: bodyName, sortable: true},
                    {accessor: 'expiration', title: 'Expiration', render: bodyExpiration},
                    {accessor: 'likelihood', title: 'Likelihood', render: bodyChance},
                    {accessor: 'difficulty', title: 'Difficulty'},
                    {accessor: 'type', title: 'Type', render: bodyType},
                    {accessor: 'reward', title: 'Reward', render: bodyReward},
                    {accessor: 'notes', title: 'Notes'},
                ]}
            />
        </Modal>
    );
}

const distanceLines = [33, 65, 100];


const drawMap = (canvas, location) => {
    const context = canvas.getContext('2d');
    context.lineWidth = 2;

    if(location !== null) {
        let x = parseInt(location.coordinates.galaxy.attributes.x)+500;
        let y = -parseInt(location.coordinates.galaxy.attributes.y)+500;
        context.strokeStyle = 'rgba(178,27,27,0.85)';
        for(let [mx, my] of scanDirs) {
            context.beginPath();
            context.moveTo(x,y);
            context.lineTo(x+(1000*mx), y+(1000*my));
            context.stroke();
        }

        context.setLineDash([5, 5]);
        for(let d of distanceLines) {
            context.beginPath();
            context.rect(x - d, y - d, d * 2, d * 2);
            context.stroke();
        }
        context.setLineDash([]);
    }


    return () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
    }
}
const HeatMapCanvas = ({current, showCurrentLines}) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            return drawMap(canvas, showCurrentLines ? current : null);
        }
    }, [current, showCurrentLines]);


    return (
        <canvas className={style.canvas} ref={canvasRef} width={1000} height={1000}></canvas>
    )
}


export default withRouteErrorBoundary(HeatMap);
